import { useAuth } from '@/contexts/AuthContext';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Skeleton } from "@/components/ui/skeleton";
import { canAccessRoute, getDefaultLandingPage, AppRole } from '@/lib/permissions';
import { toast } from 'sonner';

const ProtectedRoute = ({
  adminOnly = false,
  riderOnly = false,
  requiredRoles = []
}: {
  adminOnly?: boolean;
  riderOnly?: boolean;
  requiredRoles?: AppRole[];
} = {}) => {
  const { session, loading, isAdmin, isRider, userRoles, isEmailConfirmed } = useAuth();
  const location = useLocation();
  
  console.log('🛡️ ProtectedRoute check:', {
    path: location.pathname,
    hasSession: !!session,
    loading,
    isAdmin,
    isRider,
    isEmailConfirmed,
    userRoles: userRoles?.map(r => r.role)
  });

  // Show loading state while checking auth status OR while roles are being loaded
  // IMPORTANT: Wait for userRoles to be loaded before making redirect decisions
  // This prevents race conditions where isAdmin is false initially but becomes true after roles load
  if (loading || (session && userRoles === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  // If no session, redirect to appropriate login page
  if (!session) {
    // For customer routes, redirect to homepage with auth modal instead of /auth
    const isCustomerRoute = location.pathname.startsWith('/dashboard') || 
                           location.pathname.startsWith('/orders') || 
                           location.pathname.startsWith('/account') || 
                           location.pathname.startsWith('/wishlist') || 
                           location.pathname.startsWith('/insights') || 
                           location.pathname.startsWith('/engagement');
    
    if (isCustomerRoute) {
      return <Navigate to="/" state={{ from: location, openAuth: true }} replace />;
    }
    
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check email confirmation (Issue #133 - Enforce email verification)
  // Customers must have confirmed email to access protected routes
  if (!isAdmin && !isRider && !isEmailConfirmed) {
    // Redirect to home and open auth modal so user sees verification guidance
    return <Navigate to="/" state={{ from: location, openAuth: true }} replace />;
  }

  // If admin only route and user is not admin, redirect to home
  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // If rider only route and user is not rider, redirect to appropriate page
  if (riderOnly && !isRider) {
    // If they're admin, redirect to admin dashboard, otherwise to home
    return <Navigate to={isAdmin ? "/admin/dashboard" : "/"} replace />;
  }

  // Role-based access control
  if (requiredRoles && requiredRoles.length > 0) {
    const currentRoles = (userRoles?.map(r => r.role) || []) as AppRole[];
    const hasRequiredRole = requiredRoles.some(role => currentRoles.includes(role));

    if (!hasRequiredRole) {
      toast.error('You do not have permission to access this page');
      const defaultPage = getDefaultLandingPage(currentRoles);
      return <Navigate to={defaultPage} replace />;
    }
  }

  // Check if user can access the current route based on their roles
  if (adminOnly && userRoles && userRoles.length > 0) {
    const currentRoles = (userRoles.map(r => r.role)) as AppRole[];

    // Super admin and admin have access to everything
    const isSuperAdminOrAdmin = currentRoles.includes('super_admin') || currentRoles.includes('admin');

    if (!isSuperAdminOrAdmin && !canAccessRoute(currentRoles, location.pathname)) {
      toast.error('You do not have permission to access this page');
      const defaultPage = getDefaultLandingPage(currentRoles);
      return <Navigate to={defaultPage} replace />;
    }
  }

  // If all checks pass, render the route
  return <Outlet />;
};

export default ProtectedRoute;
